/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backgrid from 'backgrid';
import uuid from 'uuid';

import callByNeed from '../../util/callByNeed';

import './MultiButtonCell.css';
import tmplMultiButtonCell from './MultiButtonCell.hbs';

const MultiButtonCell = Backgrid.Cell.extend({
  className: 'multi-button-cell td-grid-cell-btn',

  events: {
    'click .btn-stand-alone': 'onClickStandAloneButton',
    'click .dropdown-item-click-handler': 'onClickDropdownLink'
  },

  dropdownActions: {},

  render() {
    this.$el.addClass(this.column.get('name'));
    const baseButtonClass = 'btn-primary btn-sm';
    this.$el.empty();

    let id = 0;
    for (const button of this.buttons) {
      // If button is a dropdown list of actions, get the actions, iterate
      // over them, create a new UUID for each one, and then add those
      // id/handler key-value pairs to the view property called
      // 'dropdownActions'
      let dropdownOptions = Backgrid.callByNeed(button.dropdownOptions, this, this.model);

      if (_.isArray(dropdownOptions)) {
        for (const dropdownOption of dropdownOptions) {
          if (dropdownOption.onClick && _.isFunction(dropdownOption.onClick)) {
            dropdownOption.actionId = uuid.v4();
            this.dropdownActions[dropdownOption.actionId] = dropdownOption.onClick;
          }

          if (!_.has(dropdownOption, 'renderable')) {
            dropdownOption.renderable = true;
          } else {
            dropdownOption.renderable = callByNeed(dropdownOption.renderable);
          }
        }
      }

      // Remove any non-renderable dropdown options
      if (dropdownOptions) {
        dropdownOptions = dropdownOptions.find((dropdownOption) => dropdownOption.renderable);
      }

      // Default button type to dropdown if dropdownOptions exist, link if href
      // exists, and button otherwise
      let buttonType;
      if (dropdownOptions) {
        buttonType = 'dropdown';
      } else if (button.href) {
        buttonType = 'link';
      } else {
        buttonType = 'button';
      }

      // Consider button renderable if property is not present
      let renderable;
      if (!_.has(button, 'renderable')) {
        renderable = true;
      } else {
        renderable = callByNeed(button.renderable, this, this.model);
      }

      if (renderable) {
        this.$el.append(
          tmplMultiButtonCell({
            id: id++,
            name: button.name,
            buttonClass: button.buttonClass
              ? Backgrid.callByNeed(button.buttonClass, this, this.model)
              : baseButtonClass,
            labelClass: button.labelClass,
            buttonType,
            dropdownOptions,
            href: Backgrid.callByNeed(button.href, this, this.model),
            newTab: button.newTab,
            disabled: Backgrid.callByNeed(button.disabled, this, this.model)
          })
        );

        if (this.buttonAttrs) {
          this.$el.find('button').attr(this.buttonAttrs);
        }
      }
    }

    this.delegateEvents();
    return this;
  },

  onClickStandAloneButton(e) {
    e.preventDefault();

    const handler = this.buttons[$(e.currentTarget).data('multi-button-id')].onClick;

    if (handler && _.isFunction(handler)) {
      handler(e, this.model);
    }
  },

  onClickDropdownLink(e) {
    e.preventDefault();

    const handler = this.dropdownActions[$(e.currentTarget).data('multi-button-dropdown-action-id')];

    if (handler && _.isFunction(handler)) {
      handler(e, this.model);
    }
  }
});

export default MultiButtonCell;
